import { motion } from "framer-motion";
import userExpBadge from "../assets/user-experience-badge.png";
import innovationBadge from "../assets/innovation-badge.png";
import aiImage from "../assets/ai-services/ai-illustration.svg";
import iconAutomation from "../assets/icon-automation.png";
import iconStreamline from "../assets/icon-streamline.png";
import iconDecision from "../assets/icon-decision.png";
import iconEfficiency from "../assets/icon-efficiency.png";
import iconWorkload from "../assets/icon-workload.png";
import iconWorkflow from "../assets/icon-workflow.png";
import ChatbotIcon from "../assets/ai-services/chatbot.jpg";
import AnalyticsIcon from "../assets/ai-services/ai-analytics.png";
import RecognitionIcon from "../assets/ai-services/ai-recognition.jpg";

import robot from "../assets/ai-services/aii-bot.png";
import badgeAutomation from "../assets/ai-services/badge-automation.png";
import badgeIntegration from "../assets/ai-services/badge-integration.png";
import badgeReduction from "../assets/ai-services/badge-reduction.png";
import badgeComplexity from "../assets/ai-services/badge-complexity.png";
import badgeDecision from "../assets/ai-services/badge-decision.png";
import badgeStreamline from "../assets/ai-services/badge-streamlined.png";
import grid from "../assets/ai-services/grid-pattern.jpg";
import { Helmet } from "react-helmet-async";

export default function AIServices() {
 
  const floatY = { y: [0, -8, 0] };
  const floatTransition = {
    duration: 3.2,
    repeat: Infinity,
    ease: "easeInOut",
  };

  const services = [
    {
      image: ChatbotIcon,
      title: "Chatbot",
      description:
        "AI-driven chatbots boost user happiness by offering immediate and customized customer help.",
    },
    {
      image: AnalyticsIcon,
      title: "Analytics",
      description:
        "Marketing and corporate decisions by forecasting user behavior and market trends.",
    },
    {
      image: RecognitionIcon,
      title: "Recognition",
      description:
        "Enhances accessibility and user engagement on all platforms with Image and Voice recognition.",
    },
  ];
  const cards = [
    {
      icon: iconAutomation,
      text: "Complex tasks are automated at Lentera with AI integration.",
    },
    {
      icon: iconStreamline,
      text: "Operations are efficiently streamlined using it.",
    },
    {
      icon: iconDecision,
      text: "Artificial intelligence improves decision-making.",
    },
    {
      icon: iconEfficiency,
      text: "Increased operational efficiency results from the integration.",
    },
    {
      icon: iconWorkload,
      text: "Workload from manual labor is decreased.",
    },
    {
      icon: iconWorkflow,
      text: "Workflow management is improved by AI-driven automation.",
    },
  ];
  
  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.2 },
    }),
  };

  return (
    <>
      <Helmet>
        <title>
          AI Solutions | AI Integration & Automation by Lentera Technologies
        </title>
        <meta
          name="description"
          content="Lentera Technologies delivers AI solutions with machine learning, automation, chatbots, analytics and recognition to enhance user experience and efficiency."
        />
        <meta
          name="keywords"
          content=" AI solutions, Lentera Technologies, AI integration, machine learning, AI automation, chatbot solutions, AI analytics, voice recognition, image recognition."
        />
      </Helmet>

      <section className="relative bg-black text-white overflow-hidden w-full h-[450px] md:h-[600px] lg:h-[746px] px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 xl:pt-20 pb-0">
        {/* Grid Background */}
        <img
          src={grid}
          alt="Grid Background"
          className="absolute top-0 left-0 w-full h-full object-cover opacity-100 pointer-events-none z-0"
        />

        {/* Gradient Blurs */}
        <div className="absolute bottom-0 left-0 h-full w-[100px] sm:w-[150px] lg:w-[200px] bg-gradient-to-r from-[] to-transparent blur-2xl z-10 pointer-events-none" />
        <div className="absolute bottom-0 right-0 h-full w-[100px] sm:w-[150px] lg:w-[200px] bg-gradient-to-l from-[] to-transparent blur-2xl z-10 pointer-events-none" />

        {/* Heading & Paragraph */}
        <div className="relative z-20 max-w-[637px] mx-auto text-center flex flex-col items-center gap-10">
          <h2 className="text-[32px] md:text-[48px] xl:text-[64px] font-regular leading-none">
            <span className="bg-gradient-to-r from-[#7B41E6] to-[#ED72EF] text-transparent bg-clip-text">
              AI Solutions
            </span>
            <br />
            for your business
          </h2>
          <p className="bg-gradient-to-r from-[#999999] to-[#FFFFFF] text-transparent bg-clip-text text-[16px] md:text-[18px] leading-tight">
            AI solutions improve app functionality and user experience by
            utilizing
            <br className="hidden sm:block" />
            machine learning models and algorithms.
          </p>
        </div>

        {/* Robot Image aligned to section bottom edge */}
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-20">
          <img
            src={robot}
            alt="AI Robot"
            className="w-[260px] sm:w-[400px] md:w-[500px] lg:w-[577px] h-auto"
          />
        </div>

        {/* Floating Badges */}
        <motion.img
          src={badgeReduction}
          alt="Reduce Labor Cost"
          className="absolute w-[90px] sm:w-[120px] md:w-[180px] lg:w-[220px] top-[45%] left-[27%] z-30"
          animate={floatY}
          transition={{ ...floatTransition, duration: 3.1 }}
        />
        <motion.img
          src={badgeComplexity}
          alt="Minimized Complexity"
          className="absolute w-[90px] sm:w-[120px] md:w-[180px] lg:w-[220px] top-[45%] right-[27%] z-30"
          animate={floatY}
          transition={{ ...floatTransition, duration: 3.2 }}
        />
        <motion.img
          src={badgeIntegration}
          alt="Integration"
          className="absolute w-[100px] sm:w-[180px] md:w-[230px] lg:w-[280px] top-[57%] left-[16%] z-30"
          animate={floatY}
          transition={{ ...floatTransition, duration: 2.8 }}
        />
        <motion.img
          src={badgeAutomation}
          alt="Automation"
          className="absolute w-[110px] sm:w-[160px] md:w-[230px] lg:w-[350px] top-[70%] left-[4%] z-30"
          animate={floatY}
          transition={{ ...floatTransition, duration: 3.3 }}
        />
        <motion.img
          src={badgeDecision}
          alt="Decision Making"
          className="absolute w-[100px] sm:w-[140px] md:w-[200px] lg:w-[240px] top-[57%] right-[18%] z-30"
          animate={floatY}
          transition={{ ...floatTransition, duration: 3.1 }}
        />
        <motion.img
          src={badgeStreamline}
          alt="Streamline Operation"
          className="absolute w-[110px] sm:w-[160px] md:w-[230px] lg:w-[350px] top-[70%] right-[4%] z-30"
          animate={floatY}
          transition={{ ...floatTransition, duration: 3.4 }}
        />
      </section>

      <section className="bg-black text-white font-poppins py-8 md:py-12 xl:py-20 px-4 md:px-8 xl:px-16 flex flex-col items-center">
        <section className="text-white font-poppins px-0 sm:px-4 flex flex-col items-center mb-6 sm:mb-8 md:mb-12 xl:mb-20">
          {/* Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-[24px] sm:text-[32px] xl:text-[48px] font-semibold text-center mb-4 sm:mb-6 lg:mb-8"
          >
            AI in Lentera
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center font-normal text-[14px] sm:text-[16px] text-gray-200 max-w-3xl mb-8 xl:mb-12"
          >
            Lentera’s integration of AI services expands our digital
            capabilities by utilizing <br className="hidden md:block" />
            cutting-edge AI technologies.
          </motion.p>

          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 xl:gap-10 max-w-6xl w-full mx-auto">
            {[
              {
                title: "User experience",
                image: userExpBadge,
                text: "With the help of these services, we can streamline workflows, extract insightful information from data and provide individualized user experiences for all of our platforms.",
              },
              {
                title: "Innovation",
                image: innovationBadge,
                text: "Lentera stimulates innovation in service delivery and customer engagement while optimizing operational efficiency through the integration of AI into our processes.",
              },
            ].map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#19004E] to-[#3A0039] rounded-xl lg:rounded-[2xl] p-6 sm:p-4 md:p-8 xl:p-12 text-center"
              >
                <img
                  loading="lazy"
                  decoding="async"
                  src={card.image}
                  alt={card.title}
                  className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6"
                />
                <h3 className="text-[18px] sm:text-[20px] xl:text-[24px] font-semibold text-white mb-4">
                  {card.title}
                </h3>
                <p className="font-normal text-[14px] sm:text-[16px] text-gray-100 leading-relaxed">
                  {card.text}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="text-white w-full flex flex-col items-center pt-10 xl:pt-10 sm:mb-8 md:mb-12 xl:mb-20">
          {/* Section Heading */}
          <div className="text-center mb-8 xl:mb-12">
            <h2 className="text-[24px] sm:text-[32px] xl:text-[48px] font-semibold mb-4 sm:mb-6 lg:mb-8 leading-tight">
              How AI works in Lentera
            </h2>

            <motion.p
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center  font-regular text-[14px] sm:text-[16px] mb-0 max-w-3xl"
            >
              Analyzing user data and behaviors to provide personalized
              experiences, automate tasks
              <br className="hidden lg:block" />
              and enhance decision-making.
            </motion.p>
          </div>

          {/* Card Section */}

          <div className="w-full bg-[#261237] rounded-xl xl:rounded-[48px] px-6 md:px-8 xl:px-12 py-6 md:py-8 xl:py-12 flex flex-col lg:flex-row items-center justify-between gap-10">
            {/* Left Content - 60% */}
            <div className="w-full lg:w-[60%]">
              <span className="inline-block bg-[#A88BFF] text-white text-[14px] sm:text-[16px] font-semibold px-4 py-1 rounded-md mb-4">
                AI integration
              </span>
              <h3 className="text-white font-semibold text-[24px] sm:text-[32px] xl:text-[48px] mb-4 sm:mb-6 lg:mb-8 leading-tight">
                Digital Capabilities Via <br /> AI Integration
              </h3>
              <p className="text-[14px] sm:text-[16px] font-regular leading-normal mb-0">
                At Lentera, we use AI tools like NLP and machine learning to
                simplify workflows and improve decisions. This keeps our
                solutions scalable, reliable and at the cutting edge of tech.
                <br />
                <br />
                This strategy guarantees that Lentera stays at the forefront of
                technological innovation while also improving the scalability
                and dependability of our solutions.
              </p>
            </div>

            {/* Right Image - 60% */}
            <div className="w-full lg:w-[40%] flex justify-center">
              <img
                loading="lazy"
                decoding="async"
                src={aiImage} // ensure proper import
                alt="AI Integration Illustration"
                className="w-full max-w-[350px] object-contain"
              />
            </div>
          </div>
        </section>

        <section className="w-full text-white py-8 xl:py-16 sm:mb-8 md:mb-12 xl:mb-20">
          <div className="">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 mb-8 md:mb-12 xl:mb-20">
              <div className="w-full lg:w-1/2">
                <h2 className="text-[24px] sm:text-[32px] xl:text-[48px] font-semibold leading-tight">
                  Influence of <br />
                  <span className="bg-gradient-to-r from-[#7C41E6] to-[#E26DEE] text-transparent bg-clip-text">
                    Artificial Intelligence
                  </span>
                  <br /> in Lentera
                </h2>
              </div>
              <div className="w-full lg:w-1/2">
                <p className="font-regular text-[14px] sm:text-[16px] mb-0 opacity-[80%]">
                  Artificial Intelligence has significantly enhanced Lentera’s
                  capabilities by enabling smarter decision-making,
                  personalizing user experiences and streamlining operations
                  through automation and data-driven insights.
                </p>
              </div>
            </div>

            {/* Cards Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cards.map((card, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-[#2A2A2A] rounded-2xl p-4 flex gap-4 items-center"
                >
                  <div className="w-14 h-14 flex items-center justify-center shrink-0">
                    <img
                      loading="lazy"
                      decoding="async"
                      src={card.icon}
                      alt="icon"
                      className="w-16 h-16 object-contain"
                    />
                  </div>
                  <p className="text-sm sm:text-base text-white leading-relaxed">
                    {card.text}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-[90%] lg:w-[80%] mx-auto text-white mb-2 xl:mb-5">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-[1200px] font-semibold text-center"
          >
            <h2 className="text-[24px] sm:text-[32px] xl:text-[48px] font-semibold leading-tight mb-8 md:mb-12 xl:mb-16">
              Innovations in AI improving <br className="hidden sm:block" />
              user experience
            </h2>
          </motion.div>

          {/* Services */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="w-[80%] md:w-full mx-auto"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                custom={index}
              >
                <img
                  loading="lazy"
                  decoding="async"
                  src={service.image}
                  alt={service.title}
                  className="w-full h-auto mb-6"
                />
                <div>
                  <h3 className="text-[18px] sm:text-[20px] lg:text-[24px] font-semibold leading-normal mb-4">
                    {service.title}
                  </h3>
                  <p className="font-regular text-[14px] sm:text-[16px] mb-0 leading-normal">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </section>
    </>
  );
}
